import * as React from 'react'
import { Outlet } from 'react-router-dom'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import hasFeature from 'modules/core/lib/hasFeature'

import { useContractVersions } from 'modules/contracts/lib/contractVersionsContext'
import { useContractVersionsPayrollPolicies } from 'modules/contracts/store/collections/contractVersionsPayrollPolicies'
import type ContractVersion from 'modules/contracts/store/models/ContractVersion'
import type Employee from 'modules/employees/store/models/Employee'
import jobLevelsCollection from 'modules/job_catalog/store/collections/levels'
import jobRolesCollection from 'modules/job_catalog/store/collections/roles'
import { usePolicies } from 'modules/payroll_bundle/store/collections/policies'

interface BodyProps {
  employee: Employee
  contractVersion: ContractVersion | null | undefined
}

const Body = ({ employee, contractVersion }: BodyProps) => {
  const contractVersions = useContractVersions()
  const policies = usePolicies()
  const contractVersionsPayrollPolicies = useContractVersionsPayrollPolicies()
  const employeeId = employee.attributes.id
  const contractVersionId = contractVersion?.attributes.id

  const { loading: loadingContractVersions } = useAsyncData(() => {
    return contractVersions.fetch({
      data: {
        employee_ids: [employeeId],
        with_metadata: true,
        include_denormalized_data: true,
      },
    })
  }, [employeeId])

  const { loading: loadingJobRolesAndLevels } = useAsyncData(() => {
    if (!hasFeature('job_catalog')) {
      return Promise.resolve()
    }
    return Promise.all([jobRolesCollection.fetch(), jobLevelsCollection.fetch()])
  }, [])

  const { loading: loadingContractVersionsPayrollPolicies } = useAsyncData(() => {
    if (!contractVersionId) {
      return Promise.resolve()
    }

    return contractVersionsPayrollPolicies.fetch({
      data: {
        contract_version_ids: [contractVersionId],
      },
    })
  }, [contractVersionId])

  const { loading: loadingPolicies } = useAsyncData(() => {
    return policies.fetch({ data: { include_archived: true } })
  }, [])

  if (
    loadingContractVersions ||
    loadingContractVersionsPayrollPolicies ||
    loadingPolicies ||
    loadingJobRolesAndLevels
  ) {
    return <Loading />
  }

  return <Outlet context={{ contractVersion, employee }} />
}

export default observer(Body)
